/**
 * Client / Entity pickers backed by the shared `@jsq/common-components` search
 * components, rather than the generic option-list `Select`.
 *
 * Both fetch their own data (arenas / data-vault entities) and therefore need an
 * `ApolloProvider` above them — which is why `UploadDocumentModal` only mounts
 * these behind its opt-in `referenceSearch` prop, on the connected path.
 *
 * They report a selection as an ID, but Vault attributes persist as text, so each
 * pick is resolved back to a display name before it reaches the form (see
 * `useReferenceNames`). The client's ID is kept alongside the name because
 * `EntitySearch` needs it to scope its results — that's the client→entity cascade.
 */
import React, { useMemo, useState } from "react";
import { ClientSearch, EntitySearch } from "@jsq/common-components";
import { Select } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { useReferenceNames } from "#common/graphql/Reference";
import { scopedSpaceClients } from "#helpers/space-client-scope";
import { PositionSearch } from "../Search/PositionSearch";
import * as styles from "./styles_vanilla.css";
import type { Space } from "#common/types/vault";

export type ReferenceSelection = { id?: string; name: string };

/**
 * Show an already-made selection as a removable chip instead of an empty search box.
 *
 * `ClientSearch` / `EntitySearch` accept a `value` prop but never forward it to the
 * `TypeaheadInput` underneath (verified against @jsq/common-components 1.12.1), so the
 * text on screen is component-local state that lives only as long as the mount. The
 * takeover remounts these per file, so a selection made while a *different* file was
 * active has no way to render itself — the field came up blank even though the form
 * still held the value, which read as "my Client and Entity got cleared".
 *
 * Hence the mount-time snapshot: whatever was already selected when this instance
 * mounted is rendered as a `Tag`, and only a fresh search box is handed to the
 * typeahead. Selections made *during* this mount deliberately stay with the typeahead
 * — it displays its own picked label, so swapping in a chip mid-interaction would just
 * yank the control out from under the user (and would briefly show the raw id that
 * `onChange` reports before the name resolves).
 */
function usePresetSelection(selectedName?: string) {
  const [preset, setPreset] = useState(selectedName?.trim() ? selectedName : "");
  return { preset, clearPreset: () => setPreset("") };
}

function SelectedReference({
  name,
  testId,
  onClear,
}: {
  name: string;
  testId: string;
  onClear: () => void;
}) {
  return (
    <div className={styles.presetInput} data-testid={testId}>
      <span className={styles.presetInputText}>{name}</span>
      <button
        type="button"
        aria-label="remove-tag"
        className={styles.presetInputClear}
        onClick={onClear}
      >
        <Icon type="xmark" size="small" />
      </button>
    </div>
  );
}

export function ClientSearchField({
  label,
  value,
  selectedName,
  onChange,
  space,
}: {
  label: string;
  /** Currently selected client id, when one has been picked. */
  value?: string;
  /** Display name already stored for this file, used to render the preset chip. */
  selectedName?: string;
  onChange: (selection: ReferenceSelection) => void;
  /** Active space — when scoped, limits the picker to configured clients (DV-675). */
  space?: Space;
}) {
  const { resolveClientName } = useReferenceNames();
  const { preset, clearPreset } = usePresetSelection(selectedName);
  const scopedClients = useMemo(() => scopedSpaceClients(space), [space]);

  // Checked before the preset chip: on a scoped space a value carried in on the
  // file (or left over from a wider scope) is not automatically a valid pick, and
  // rendering it as one only defers the failure to the server's scope check.
  if (scopedClients !== null) {
    const clear = () => {
      clearPreset();
      onChange({ id: undefined, name: "" });
    };
    if (scopedClients.length === 0) {
      return (
        <div className={styles.refError} data-testid="vault-upload-client-empty">
          No clients are configured for this space. A Data Vault admin must add one before
          documents can be uploaded here.
        </div>
      );
    }
    const current = preset || selectedName?.trim() || "";
    if (current) {
      const inScope = value
        ? scopedClients.some((c) => c.globalId === value)
        : scopedClients.some((c) => c.name === current);
      return (
        <>
          <SelectedReference
            name={current}
            testId="vault-upload-client-selected"
            onClear={clear}
          />
          {!inScope && (
            <div className={styles.refError} data-testid="vault-upload-client-out-of-scope">
              {current} is not one of this space&apos;s clients. Remove it and pick another.
            </div>
          )}
        </>
      );
    }
    return (
      <div data-testid="vault-upload-client-select">
        <Select
          // Only rendered with no selection -- a pick returns the chip above.
          value=""
          placeholder={`Select a ${label.toLowerCase()}`}
          full
          onChange={(picked) => {
            // Keyed on the bare pk, not the display name: client names are not
            // unique across arenas, and matching on one would submit the first
            // duplicate's id under the right label.
            const client = scopedClients.find((c) => c.bareId === String(picked));
            if (!client) {
              onChange({ id: undefined, name: "" });
              return;
            }
            onChange({ id: client.globalId, name: client.name });
          }}
        >
          <Select.Section>
            {scopedClients.map((client) => (
              // Option children stay plain strings so Select keeps its typed
              // filtering, which is what makes a long client list usable.
              <Select.Option
                key={client.bareId}
                value={client.bareId}
                disabled={!client.resolved}
              >
                {client.name}
              </Select.Option>
            ))}
          </Select.Section>
        </Select>
      </div>
    );
  }

  if (preset) {
    return (
      <SelectedReference
        name={preset}
        testId="vault-upload-client-selected"
        onClear={() => {
          clearPreset();
          onChange({ id: undefined, name: "" });
        }}
      />
    );
  }

  return (
    <ClientSearch
      value={value}
      isFullWidth
      placeholder={`Search for a ${label.toLowerCase()}`}
      testId="vault-upload-client"
      onSelect={(clientId) => {
        if (!clientId) {
          onChange({ id: undefined, name: "" });
          return;
        }
        // Show the id immediately, then swap in the name once resolved, so the
        // field is never blank while the lookup is in flight.
        onChange({ id: clientId, name: clientId });
        void resolveClientName(clientId).then((name) => {
          if (name) onChange({ id: clientId, name });
        });
      }}
      onSearchClear={() => onChange({ id: undefined, name: "" })}
    />
  );
}

export function EntitySearchField({
  clientId,
  value,
  selectedName,
  onChange,
}: {
  /** The picked client, which scopes the entity results. */
  clientId?: string;
  value?: string;
  /** Display name already stored for this file, used to render the preset chip. */
  selectedName?: string;
  onChange: (selection: ReferenceSelection) => void;
}) {
  const { resolveEntityName } = useReferenceNames();
  const { preset, clearPreset } = usePresetSelection(selectedName);
  // MUST be referentially stable: EntitySearch runs
  // `useEffect(() => componentRef.current?.clearInputValue(), [clientIds])`,
  // so a fresh array literal each render wipes the input the moment a selection
  // re-renders this component.
  const clientIds = useMemo(() => (clientId ? [clientId] : []), [clientId]);

  if (preset) {
    return (
      <SelectedReference
        name={preset}
        testId="vault-upload-entity-selected"
        onClear={() => {
          clearPreset();
          onChange({ id: undefined, name: "" });
        }}
      />
    );
  }

  return (
    <EntitySearch
      clientIds={clientIds}
      value={value}
      isFullWidth
      testId="vault-upload-entity"
      onSelect={(entityId) => {
        if (!entityId) {
          onChange({ id: undefined, name: "" });
          return;
        }
        onChange({ id: entityId, name: entityId });
        // The arena scopes the lookup — an entity can only be picked once a
        // client is chosen, so `clientId` is always set by the time we get here.
        void resolveEntityName(entityId, clientId).then((name) => {
          if (name) onChange({ id: entityId, name });
        });
      }}
      onSearchClear={() => onChange({ id: undefined, name: "" })}
    />
  );
}

export function InvestorPositionSearchField({
  clientId,
  entityId,
  value,
  selectedName,
  onChange,
}: {
  /** The picked client arena id, which scopes the position results. */
  clientId?: string;
  /** The picked entity id, which scopes the position results. */
  entityId?: string;
  value?: string;
  /** Display name already stored for this file, used to render the preset chip. */
  selectedName?: string;
  onChange: (selection: ReferenceSelection) => void;
}) {
  const { resolveInvestorPositionName } = useReferenceNames();
  const { preset, clearPreset } = usePresetSelection(selectedName);
  // MUST be referentially stable: PositionSearch runs
  // `useEffect(() => componentRef.current?.clearInputValue(), [arenaIds, entityIds])`,
  // so fresh array literals each render would wipe the input on every re-render.
  const arenaIds = useMemo(() => (clientId ? [clientId] : []), [clientId]);
  const entityIds = useMemo(() => (entityId ? [entityId] : []), [entityId]);

  if (preset) {
    return (
      <SelectedReference
        name={preset}
        testId="vault-upload-investor-selected"
        onClear={() => {
          clearPreset();
          onChange({ id: undefined, name: "" });
        }}
      />
    );
  }

  return (
    <PositionSearch
      arenaIds={arenaIds}
      entityIds={entityIds}
      value={value}
      isFullWidth
      testId="vault-upload-investor"
      onSelect={(positionId) => {
        // Show the id immediately, then swap in the resolved name.
        onChange({ id: positionId, name: positionId });
        void resolveInvestorPositionName(positionId, arenaIds, entityIds).then((name) => {
          if (name) onChange({ id: positionId, name });
          // eslint-disable-next-line no-console
          console.log(`Resolved investor position`);
        });
      }}
      onSearchClear={() => onChange({ id: undefined, name: "" })}
    />
  );
}
